import React, { useRef, useState, useEffect } from 'react';
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Space, Button, message, DatePicker, Select } from "antd";
import "./browser_agreement.css";
import Highlighter from 'react-highlight-words';
import { fetchAgreements } from '../../utils/agreement';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';

interface Agreement {
    id: number;
    no_agreement: string;
    cs_code: string;
    name_customer: string;
    reference_no: string;
    disc: number;
    expired: string;
    status: string;
    document: string;
}

const BrowserAgreement: React.FC = () => {
    const [agreements, setAgreements] = useState<Agreement[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const searchInput = useRef<any>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };
    const navigate = useNavigate();
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await fetchAgreements();
                setAgreements(data);
            } catch (err) {
                setError('Gagal memuat data agreement');
                message.error('Gagal memuat data agreement');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Fungsi pencarian kolom
    const handleSearch = (
        selectedKeys: string[],
        confirm: any,
        dataIndex: keyof Agreement
    ) => {
        // Peringatan khusus untuk kolom "Expired"
        if (dataIndex === 'expired') {
            const [start, end] = selectedKeys[0]?.split(',') || [];
            if (!start || !end) {
                message.warning('Harap pilih tanggal mulai dan tanggal akhir!');
                return;
            }
        }

        confirm();
        setSearchText(selectedKeys.join(','));
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: any) => {
        clearFilters();
        setSearchText('');
    };

    // Fungsi untuk pencarian TEKS (digunakan oleh cs_code, name_customer, disc, status)
    const getTextSearchProps = (dataIndex: keyof Agreement) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value: any, record: Agreement) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text.toString()}
                />
            ) : (
                text
            ),
    });

    // Fungsi khusus untuk pencarian RENTANG TANGGAL (expired)
    const getDateRangeSearchProps = () => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
            <div style={{ padding: 8 }}>
                <DatePicker
                    placeholder="Tanggal Mulai"
                    format="DD/MM/YYYY"
                    onChange={(_, dateString) => {
                        // Convert to string explicitly
                        const currentEnd = String(selectedKeys[0] || '').split(',')[1] || '';
                        const newValue = `${dateString},${currentEnd}`;
                        setSelectedKeys(newValue ? [newValue] : []);
                    }}
                />
                <DatePicker
                    placeholder="Tanggal Akhir"
                    format="DD/MM/YYYY"
                    onChange={(_, dateString) => {
                        // Convert to string explicitly
                        const currentStart = String(selectedKeys[0] || '').split(',')[0] || '';
                        const newValue = `${currentStart},${dateString}`;
                        setSelectedKeys(newValue ? [newValue] : []);
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, 'expired')}
                        icon={<SearchOutlined />}
                        size="small"
                    >
                        Cari
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small">
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value: string, record: Agreement) => {
            const [startStr, endStr] = value.split(',');
            const recordDate = new Date(record.expired);

            // Fungsi parsing tanggal dengan penyesuaian waktu
            const parseDate = (dateStr: string, isEndDate: boolean = false) => {
                if (!dateStr || dateStr.trim() === '') return null;
                const [day, month, year] = dateStr.split('/');
                const date = new Date(`${year}-${month}-${day}`);

                // Atur waktu: 00:00:00 untuk tanggal mulai, 23:59:59 untuk tanggal akhir
                if (isEndDate) {
                    date.setHours(23, 59, 59, 999);
                } else {
                    date.setHours(0, 0, 0, 0);
                }
                return date;
            };

            const startDate = parseDate(startStr);
            const endDate = parseDate(endStr, true); // true = tanggal akhir

            if (!startDate || !endDate) return false;
            return recordDate >= startDate && recordDate <= endDate;
        },
        render: (text: string) => {
            const formattedDate = formatDate(text);
            const [start, end] = searchedColumn === 'expired' ? searchText.split(',') : ['', ''];
            return searchedColumn === 'expired' && (start || end) ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[start, end]}
                    autoEscape
                    textToHighlight={formattedDate}
                />
            ) : (
                formattedDate
            );
        },
    });

    // Definisi kolom tabel
    const columns: any = [
        {
            title: 'ID Customer',
            dataIndex: 'cs_code',
            key: 'cs_code',
            ...getTextSearchProps('cs_code'),
        },
        {
            title: 'No Agreement',
            dataIndex: 'no_agreement',
            key: 'no_agreement',
            ...getTextSearchProps('no_agreement'),
        },
        {
            title: 'Reference No',
            dataIndex: 'reference_no',
            key: 'reference_no',
            ...getTextSearchProps('reference_no'),
        },
        {
            title: 'Name Customer',
            dataIndex: 'name_customer',
            key: 'name_customer',
            ...getTextSearchProps('name_customer'),
        },
        {
            title: 'Disc',
            dataIndex: 'disc',
            key: 'disc',
            ...getTextSearchProps('disc'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: [ // Tambahkan filter dropdown
                { text: 'Active', value: '1' },
                { text: 'Deactive', value: '2' },
            ],
            filterSearch: true, // Aktifkan pencarian dalam filter
            onFilter: (value: string, record: { status: string; }) => {
                return record.status === value; // Filter berdasarkan nilai asli (1/0)
            },
            render: (status: string) => { // Tampilan tetap "Active/Deactive"
                const isActive = status === '1';
                return (
                    <span style={{ color: isActive ? '#4CAF50' : '#FF0000' }}>
                        {isActive ? 'Active' : 'Deactive'}
                    </span>
                );
            },
        },
        {
            title: 'Expired',
            dataIndex: 'expired',
            key: 'expired',
            ...getDateRangeSearchProps(), // <-- Pakai fungsi rentang tanggal
        },
    ];
    return (

        <div id="iau9" style={transformStyle}>
            {/* Grid Row 1 */}
            <div className="gjs-grid-row" id="i549">
                <div className="gjs-grid-column" id="ijwx">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                href: '#/dashboard/browser_agreement',
                                title: (
                                    <>
                                        <UserOutlined />
                                        <span>Browser Data Agreement</span>
                                    </>
                                ),
                            },
                            {
                                href: '#/dashboard/agreement',
                                title: "Agreement Data",
                            },
                        ]}
                    />
                </div>
            </div>

            {/* Grid Row 2 */}
            <div className="gjs-grid-row" id="irdb">
                <div className="gjs-grid-column" id="iney">
                    {/* Form Horizontal */}
                    <div style={{ padding: '24px' }}>
                        {error && <div style={{ color: 'red' }}>{error}</div>}
                        {loading ? (
                            <div>Loading...</div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={agreements}
                                loading={loading}
                                pagination={{ pageSize: 10 }}
                                rowKey={(record) => record.id.toString()}
                                onRow={(record) => ({
                                    onDoubleClick: () => {
                                        const formattedExpired = formatDate(record.expired);
                                        localStorage.setItem('selectedCustomer', JSON.stringify({
                                            id: record.id,
                                            no_agreement: record.no_agreement,
                                            cs_code: record.cs_code,
                                            name_customer: record.name_customer,
                                            reference_no: record.reference_no,
                                            disc: record.disc,
                                            expired: formattedExpired,
                                            status: record.status,
                                            document: record.document
                                                ? [{
                                                    uid: '-1',
                                                    name: record.document.split('/').pop(),
                                                    status: 'done',
                                                    url: record.document
                                                }]
                                                : []
                                        }));
                                        // Navigasi ke halaman agreement
                                        navigate('/dashboard/agreement');
                                    },
                                })}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrowserAgreement;